
// Fix: Clean modular Firestore imports
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  deleteDoc, 
  writeBatch, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter, 
  endBefore,
  limitToLast, 
  getDoc, 
  updateDoc, 
  Timestamp, 
  deleteField 
} from "firebase/firestore";
import { getDb } from "../lib/firebase";
import { 
  Client, BillingRecord, BankDeposit, ReconciliationRecord, MatchStatus,
  UnionStatementBatch, UnionStatementRecord, ReportTemplate, AutoOtherRule, RecurringPayment, Match, DensaiReservation, UnionPaymentTerm
} from "../types";
import { runAutoMatchingWorker } from "./workerService";

const COLS = {
  CLIENTS: 'clients',
  BILLING: 'billing_records',
  DEPOSITS: 'bank_deposits',
  MATCHES: 'matches',
  UNION_BATCHES: 'union_batches',
  UNION_RECORDS: 'union_records',
  TEMPLATES: 'report_templates',
  RULES_AUTO: 'auto_other_rules',
  RULES_RECURRING: 'recurring_payments',
  DENSAI_RESERVATIONS: 'densai_reservations',
  SYSTEM_SETTINGS: 'system_settings',
  UNMATCHED_HISTORY: 'unmatched_history', // 新設: 紐付け解除履歴
  UNION_PAYMENT_TERMS: 'union_payment_terms' // 支払サイクル設定（翌々月・翌々々月払い）
};

const BATCH_LIMIT = 450;
const generateUuid = () => crypto.randomUUID();
const sanitize = (obj: any) => JSON.parse(JSON.stringify(obj));

// ロギング用ヘルパー
const log = (msg: string, data?: any) => {
    console.log(`[Reconciliation System] ${msg}`, data || '');
};

/**
 * 正規表現のエスケープ
 */
function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 基本的な正規化（異体字統一、全角半角統一、記号除去）
 * ※法人格判定のためにカッコは残す
 */
export function normalizeStringSimple(str: string) {
  if (!str) return '';
  let val = str;
  
  // 0. 異体字・旧字体・CJK互換文字の統一
  val = val.replace(/濵/g, '浜').replace(/濱/g, '浜').replace(/髙/g, '高').replace(/﨑/g, '崎');
  val = val.replace(/國/g, '国').replace(/㐂/g, '喜').replace(/靑/g, '青').replace(/纊/g, '絋');
  val = val.replace(/⻄/g, '西').replace(/⻑/g, '長').replace(/⻘/g, '青').replace(/⻝/g, '食');
  val = val.replace(/鷗/g, '鴎').replace(/櫻/g, '桜').replace(/齋/g, '斎').replace(/藤/g, '藤');
  val = val.replace(/澤/g, '沢').replace(/邊/g, '辺').replace(/邉/g, '辺').replace(/廣/g, '広');
  val = val.replace(/與/g, '与').replace(/龍/g, '竜').replace(/寶/g, '宝').replace(/實/g, '実');
  val = val.replace(/藏/g, '蔵');

  // 1. 半角カナ -> 全角カナ
  // ※あ行の通常サイズ（ｱ ｲ ｳ ｴ ｵ）も含めて漏れなく変換する。
  //   以前は ｱ-ｵ と ｦ が変換対象から外れており、半角カナで登録された得意先のカナ照合で
  //   先頭1文字が不一致となり88系で候補ゼロ→意図しない得意先にフォールバックする不具合があった。
  const kanaMap: { [key: string]: string } = {
    'ｱ': 'ア', 'ｲ': 'イ', 'ｳ': 'ウ', 'ｴ': 'エ', 'ｵ': 'オ',
    'ｶ': 'カ', 'ｷ': 'キ', 'ｸ': 'ク', 'ｹ': 'ケ', 'ｺ': 'コ', 'ｻ': 'サ', 'ｼ': 'シ', 'ｽ': 'ス', 'ｾ': 'セ', 'ｿ': 'ソ',
    'ﾀ': 'タ', 'ﾁ': 'チ', 'ﾂ': 'ツ', 'ﾃ': 'テ', 'ﾄ': 'ト', 'ﾅ': 'ナ', 'ﾆ': 'ニ', 'ﾇ': 'ヌ', 'ﾈ': 'ネ', 'ﾉ': 'ノ',
    'ﾊ': 'ハ', 'ﾋ': 'ヒ', 'ﾌ': 'フ', 'ﾍ': 'ヘ', 'ﾎ': 'ホ', 'ﾏ': 'マ', 'ﾐ': 'ミ', 'ﾑ': 'ム', 'ﾒ': 'メ', 'ﾓ': 'モ',
    'ﾔ': 'ヤ', 'ﾕ': 'ユ', 'ﾖ': 'ヨ', 'ﾗ': 'ラ', 'ﾘ': 'リ', 'ﾙ': 'ル', 'ﾚ': 'レ', 'ﾛ': 'ロ', 'ﾜ': 'ワ', 'ｦ': 'ヲ', 'ﾝ': 'ン',
    'ｧ': 'ア', 'ｨ': 'イ', 'ｩ': 'ウ', 'ｪ': 'エ', 'ｫ': 'オ', 'ｬ': 'ヤ', 'ｭ': 'ユ', 'ｮ': 'ヨ', 'ｯ': 'ツ', 'ﾞ': '', 'ﾟ': ''
  };
  // 半角カナブロック全域（U+FF66=ｦ 〜 U+FF9F=ﾟ）を対象とする。
  // 長音符 ｰ (U+FF70) は map に無いためそのまま通過し、後段 6. の記号除去で削除される。
  val = val.replace(/[ｦ-ﾟ]/g, (s) => kanaMap[s] || s);
  
  // 2. 全角英数 -> 半角英数 & 大文字化
  val = val.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0)).toUpperCase();
  
  // 3. 濁点・半濁点の完全除去
  val = val.normalize('NFD').replace(/[\u3099\u309A\u0300-\u036f]/g, '');
  val = val.replace(/[ﾞﾟ]/g, '');

  // 4. 小書き文字を通常文字に
  const smallToLarge: { [key: string]: string } = {
    'ァ': 'ア', 'ィ': 'イ', 'ゥ': 'ウ', 'ェ': 'エ', 'ォ': 'オ',
    'ッ': 'ツ', 'ャ': 'ヤ', 'ュ': 'ユ', 'ョ': 'ヨ'
  };
  val = val.replace(/[ァ-ョッ]/g, (s) => smallToLarge[s] || s);

  // 5. カッコの統一 (半角カッコ -> 全角カッコ)
  val = val.replace(/\(/g, '（').replace(/\)/g, '）');

  // 6. 記号・スペースの徹底除去 (カッコは残す)
  // 削除対象: スペース, ハイフン, ドット, 中黒, スラッシュ, カンマ, 感嘆符, 疑問符, 長音符, NBSP
  val = val.replace(/[\s\u3000\u00A0\-\.・\/,，、。\*？?！!ｰ－ー]/g, '');

  return val;
}

/**
 * 照合のための正規化関数
 * 法人格を除去し、最終的な比較用文字列を生成する
 */
export function normalizeForComparison(str: string) {
  if (!str) return '';
  
  // 基本的な正規化（Simpleと同じ処理）
  let val = normalizeStringSimple(str);

  // 7. 会社形態の除去
  // カッコ付きの法人格を優先的に削除する
  const noise = [
    '株式会社', '有限会社', '合同会社', '合資会社', '一般社団法人', '財団法人',
    'カブシキガイシャ', 'ユウゲンガイシャ', 'ドウドウガイシャ',
    '（カ）', '（ユ）', '（ド）', '（シ）', '（キ）', '（ザイ）', '（シャ）',
    '（株）', '（有）', '（合）', '（資）', '（財）', '（社）',
    '（カ', '（ユ', '（ド',
    'カ）', 'ユ）', 'ド）',
    '㈱', '㈲', '㈿', '㈴', '㈾', '㈳', '㈶'
  ];
  
  // エスケープして正規表現化
  const noisePattern = noise.map(escapeRegExp).join('|');
  const noiseRegExp = new RegExp(noisePattern, 'g');
  
  val = val.replace(noiseRegExp, '');
  
  // 8. 残ったカッコを除去
  val = val.replace(/[（）]/g, '');

  // 9. 支店・工場・出張所などの拠点名とその前の地名部分を除去
  // ※除去後に2文字未満になる場合は除去しない（会社名ごと消える問題の防止）
  const branchStripped = val.replace(/[^\s]*?(工場|出張所|支店|営業所|支所|事業所|事業部|製造所|プラント).*$/g, '');
  if (branchStripped.length >= 2) val = branchStripped;

  return val;
}

/**
 * 紐付け解除履歴用のブロッキングキーを生成する
 *
 * 業務担当者が「紐付けをやめる」を実行した組み合わせを再アップロード後の自動照合で
 * ブロックする目的で使用する。
 *
 * 設計方針：
 * 業務上、毎月の請求額・入金額・日付は変動するため、それらをキーに含めると
 * 翌月以降の同じ組み合わせがブロックされない（履歴が実質1回限りの効果になる）。
 * そのため「得意先CD」と「振込人名（正規化済み）」のみを使用し、
 * 同じ得意先と同じ振込人の組み合わせは金額・月・日付に関わらず継続的にブロックする。
 */
const getBlockingKey = (type: 'billing' | 'deposit', data: any) => {
    if (type === 'billing') {
        // 請求キー：得意先CD のみ
        return (data.client_code || '').toString().trim();
    } else {
        // 入金キー：正規化された振込人名 のみ
        return normalizeForComparison(data.remitter_name || '');
    }
};

export const api = {
  reports: {
    getTemplates: async (): Promise<ReportTemplate[]> => {
      const db = getDb();
      const snap = await getDocs(collection(db, COLS.TEMPLATES));
      return snap.docs.map(doc => ({ id: doc.id, ...(doc.data() as ReportTemplate) }));
    },
    saveTemplate: async (template: ReportTemplate): Promise<void> => {
      const db = getDb();
      const id = template.id || generateUuid();
      await setDoc(doc(db, COLS.TEMPLATES, id), sanitize({ ...template, id }));
    },
    deleteTemplate: async (id: string): Promise<void> => {
      const db = getDb();
      await deleteDoc(doc(db, COLS.TEMPLATES, id));
    }
  },

  clients: {
    getAll: async (): Promise<Client[]> => {
      const db = getDb();
      const snapshot = await getDocs(collection(db, COLS.CLIENTS));
      return snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as Client) }));
    },
    save: async (client: Client): Promise<void> => {
      const db = getDb();
      const id = client.id || generateUuid();
      await setDoc(doc(db, COLS.CLIENTS, id), sanitize({ ...client, id, updated_at: new Date().toISOString() }));
    },
    delete: async (id: string): Promise<void> => {
      const db = getDb();
      await deleteDoc(doc(db, COLS.CLIENTS, id));
    },
    deleteMultipleClients: async (ids: string[]): Promise<void> => {
      const db = getDb();
      let batch = writeBatch(db);
      let count = 0;
      for (const id of ids) {
          batch.delete(doc(db, COLS.CLIENTS, id));
          count++;
          if (count % BATCH_LIMIT === 0) {
              await batch.commit();
              batch = writeBatch(db);
          }
      }
      if (count % BATCH_LIMIT !== 0) await batch.commit();
    },
    importBatch: async (clients: any[]): Promise<{ imported: number, skipped: number }> => {
        const db = getDb();
        let batch = writeBatch(db);
        let count = 0;
        for (const c of clients) {
            const id = generateUuid();
            batch.set(doc(db, COLS.CLIENTS, id), sanitize({ ...c, id, created_at: new Date().toISOString() }));
            count++;
            if (count % BATCH_LIMIT === 0) { 
                await batch.commit(); 
                batch = writeBatch(db);
            }
        }
        if (count % BATCH_LIMIT !== 0) await batch.commit();
        return { imported: count, skipped: 0 };
    },
    importBillingBatch: async (records: any[]): Promise<{ imported: number, skipped: number }> => {
        const db = getDb();
        const existingSnap = await getDocs(collection(db, COLS.BILLING));
        // 重複チェック: client_code + month + amount のカウントで管理（同一金額の複数件も許容）
        const existingCounts = new Map<string, number>();
        // 既存レコードのID逆引き: key -> [docId, ...]
        const existingIdsByKey = new Map<string, string[]>();
        existingSnap.forEach(d => {
            const data = d.data() as BillingRecord;
            const key = `${data.client_code}_${(data.import_month || '').replace(/-/g, '/')}_${Number(data.billing_balance)}`;
            existingCounts.set(key, (existingCounts.get(key) || 0) + 1);
            const ids = existingIdsByKey.get(key) || [];
            ids.push(d.id);
            existingIdsByKey.set(key, ids);
        });

        const importCounts = new Map<string, number>();
        const toImport: any[] = [];
        const toUpdate: { id: string, client_name: string }[] = [];

        for (const r of records) {
            const key = `${r.client_code}_${(r.import_month || '').replace(/-/g, '/')}_${Number(r.billing_balance)}`;
            const alreadyInDb = existingCounts.get(key) || 0;
            const alreadyInBatch = importCounts.get(key) || 0;
            if (alreadyInBatch < alreadyInDb) {
                // DB に既存の分はスキップ、ただし client_name を更新する（エンコーディング修正対応）
                const existingIds = existingIdsByKey.get(key);
                if (existingIds && existingIds[alreadyInBatch] && r.client_name) {
                    toUpdate.push({ id: existingIds[alreadyInBatch], client_name: r.client_name });
                }
                importCounts.set(key, alreadyInBatch + 1);
            } else {
                importCounts.set(key, alreadyInBatch + 1);
                toImport.push(r);
            }
        }

        let batch = writeBatch(db);
        let count = 0;

        // 既存レコードのclient_name更新
        for (const u of toUpdate) {
            batch.update(doc(db, COLS.BILLING, u.id), { client_name: u.client_name });
            count++;
            if (count % BATCH_LIMIT === 0) {
                await batch.commit();
                batch = writeBatch(db);
            }
        }
        // updateバッチに未コミット分がある場合は先にコミットする（insertバッチと混在させない）
        if (count % BATCH_LIMIT !== 0) {
            await batch.commit();
            batch = writeBatch(db);
        }
        count = 0;

        // 新規レコード追加
        for (const r of toImport) {
            const id = generateUuid();
            batch.set(doc(db, COLS.BILLING, id), sanitize({ ...r, id, import_date: new Date().toISOString() }));
            count++;
            if (count % BATCH_LIMIT === 0) {
                await batch.commit();
                batch = writeBatch(db);
            }
        }
        if (count % BATCH_LIMIT !== 0) await batch.commit();
        await api.reconciliation.runAutoMatch();
        return { imported: count, skipped: records.length - toImport.length };
    },
    getAutoOtherRules: async (): Promise<AutoOtherRule[]> => {
        const db = getDb();
        const snap = await getDocs(collection(db, COLS.RULES_AUTO));
        return snap.docs.map(d => ({ id: d.id, ...(d.data() as AutoOtherRule) }));
    },
    saveAutoRule: async (rule: AutoOtherRule): Promise<void> => {
        const db = getDb();
        const id = rule.id || generateUuid();
        await setDoc(doc(db, COLS.RULES_AUTO, id), sanitize({ ...rule, id }));
    },
    deleteAutoRule: async (id: string): Promise<void> => {
        const db = getDb();
        await deleteDoc(doc(db, COLS.RULES_AUTO, id));
    },
    deleteMultipleAutoRules: async (ids: string[]): Promise<void> => {
        const db = getDb();
        let batch = writeBatch(db);
        let count = 0;
        for (const id of ids) {
            batch.delete(doc(db, COLS.RULES_AUTO, id));
            count++;
            if (count % BATCH_LIMIT === 0) {
                await batch.commit();
                batch = writeBatch(db);
            }
        }
        if (count % BATCH_LIMIT !== 0) await batch.commit();
    },
    getRecurringPayments: async (): Promise<RecurringPayment[]> => {
        const db = getDb();
        const snap = await getDocs(collection(db, COLS.RULES_RECURRING));
        return snap.docs.map(d => ({ id: d.id, ...(d.data() as RecurringPayment) }));
    },
    saveRecurringRule: async (rule: RecurringPayment): Promise<void> => {
        const db = getDb();
        const id = rule.id || generateUuid();
        await setDoc(doc(db, COLS.RULES_RECURRING, id), sanitize({ ...rule, id }));
    },
    deleteRecurringRule: async (id: string): Promise<void> => {
        const db = getDb();
        await deleteDoc(doc(db, COLS.RULES_RECURRING, id));
    },
    deleteMultipleRecurringRules: async (ids: string[]): Promise<void> => {
        const db = getDb();
        let batch = writeBatch(db);
        let count = 0;
        for (const id of ids) {
            batch.delete(doc(db, COLS.RULES_RECURRING, id));
            count++;
            if (count % BATCH_LIMIT === 0) {
                await batch.commit();
                batch = writeBatch(db);
            }
        }
        if (count % BATCH_LIMIT !== 0) await batch.commit();
    }
  },

  paymentTerms: {
    getAll: async (): Promise<UnionPaymentTerm[]> => {
        const db = getDb();
        const snap = await getDocs(collection(db, COLS.UNION_PAYMENT_TERMS));
        return snap.docs.map(d => ({ id: d.id, ...(d.data() as UnionPaymentTerm) }));
    },
    save: async (term: UnionPaymentTerm): Promise<void> => {
        const db = getDb();
        const id = term.id || generateUuid();
        await setDoc(doc(db, COLS.UNION_PAYMENT_TERMS, id), sanitize({ ...term, id }));
    },
    delete: async (id: string): Promise<void> => {
        const db = getDb();
        await deleteDoc(doc(db, COLS.UNION_PAYMENT_TERMS, id));
    },
    deleteMultiple: async (ids: string[]): Promise<void> => {
        const db = getDb();
        let batch = writeBatch(db);
        let count = 0;
        for (const id of ids) {
            batch.delete(doc(db, COLS.UNION_PAYMENT_TERMS, id));
            count++;
            if (count % BATCH_LIMIT === 0) {
                await batch.commit();
                batch = writeBatch(db);
            }
        }
        if (count % BATCH_LIMIT !== 0) await batch.commit();
    },
    importCsv: async (rows: { client_code: string; client_name: string; payment_date: string }[]): Promise<number> => {
        const db = getDb();
        // 既存データを全削除してから再登録
        const existing = await getDocs(collection(db, COLS.UNION_PAYMENT_TERMS));
        let delBatch = writeBatch(db);
        let delCount = 0;
        existing.docs.forEach(d => {
            delBatch.delete(doc(db, COLS.UNION_PAYMENT_TERMS, d.id));
            delCount++;
            if (delCount % BATCH_LIMIT === 0) delBatch.commit();
        });
        if (delCount % BATCH_LIMIT !== 0) await delBatch.commit();

        let addBatch = writeBatch(db);
        let addCount = 0;
        for (const row of rows) {
            const id = generateUuid();
            addBatch.set(doc(db, COLS.UNION_PAYMENT_TERMS, id), sanitize({
                id,
                client_code: row.client_code,
                client_name: row.client_name,
                payment_cycle: 2, // デフォルト翌々月払い
                payment_date: row.payment_date,
                union_type: 'satsuma'
            }));
            addCount++;
            if (addCount % BATCH_LIMIT === 0) {
                await addBatch.commit();
                addBatch = writeBatch(db);
            }
        }
        if (addCount % BATCH_LIMIT !== 0) await addBatch.commit();
        return addCount;
    }
  },

  densai: {
    getReservations: async (): Promise<DensaiReservation[]> => {
        const db = getDb();
        const q = query(collection(db, COLS.DENSAI_RESERVATIONS), where('status', '==', 'pending'));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...(d.data() as DensaiReservation) }));
    },
    saveReservation: async (res: DensaiReservation): Promise<void> => {
        const db = getDb();
        const id = res.id || generateUuid();
        await setDoc(doc(db, COLS.DENSAI_RESERVATIONS, id), sanitize({ ...res, id, created_at: new Date().toISOString() }));
    },
    registerManualDensai: async (res: DensaiReservation): Promise<void> => {
        const db = getDb();
        const reservationId = res.id || generateUuid();
        const reservationData = {
            ...res,
            id: reservationId,
            status: 'pending', 
            linked_deposit_id: undefined, 
            created_at: new Date().toISOString()
        };
        await setDoc(doc(db, COLS.DENSAI_RESERVATIONS, reservationId), sanitize(reservationData));
    },
    deleteReservation: async (id: string): Promise<void> => {
        const db = getDb();
        await deleteDoc(doc(db, COLS.DENSAI_RESERVATIONS, id));
    },
    deleteMultipleReservations: async (ids: string[]): Promise<void> => {
        const db = getDb();
        let batch = writeBatch(db);
        let count = 0;
        for (const id of ids) {
            batch.delete(doc(db, COLS.DENSAI_RESERVATIONS, id));
            count++;
            if (count % BATCH_LIMIT === 0) {
                await batch.commit();
                batch = writeBatch(db);
            }
        }
        if (count % BATCH_LIMIT !== 0) await batch.commit();
    },
    checkByRecordNumber: async (recordNumber: string): Promise<boolean> => {
        const db = getDb();
        const q = query(collection(db, COLS.DENSAI_RESERVATIONS), where('record_number', '==', recordNumber));
        const snap = await getDocs(q);
        return !snap.empty;
    },
    batchCreateReservations: async (reservations: any[]): Promise<number> => {
        const db = getDb();
        let batch = writeBatch(db);
        let count = 0;
        for (const r of reservations) {
            const id = generateUuid();
            const q = query(collection(db, COLS.DENSAI_RESERVATIONS), where('record_number', '==', r.record_number));
            const existing = await getDocs(q);
            if (!existing.empty) continue;

            batch.set(doc(db, COLS.DENSAI_RESERVATIONS, id), sanitize({ ...r, id, status: 'pending', created_at: new Date().toISOString() }));
            count++;
            if (count % BATCH_LIMIT === 0) {
                await batch.commit();
                batch = writeBatch(db);
            }
        }
        if (count % BATCH_LIMIT !== 0) await batch.commit();
        return count;
    }
  },

  reconciliation: {
    getAvailableMonths: async (): Promise<string[]> => {
        const db = getDb();
        const months = new Set<string>();
        const billingSnap = await getDocs(collection(db, COLS.BILLING));
        billingSnap.forEach(doc => {
            const m = (doc.data() as BillingRecord).import_month;
            if (m) months.add(m.replace(/-/g, '/').substring(0, 7));
        });
        const depositsSnap = await getDocs(collection(db, COLS.DEPOSITS));
        depositsSnap.forEach(doc => {
            const d = (doc.data() as BankDeposit).deposit_date;
            if (d && d.length >= 7) months.add(d.replace(/-/g, '/').substring(0, 7));
        });
        const densaiSnap = await getDocs(query(collection(db, COLS.DENSAI_RESERVATIONS), where('status', '==', 'pending')));
        densaiSnap.forEach(doc => {
            const d = (doc.data() as DensaiReservation).payment_date;
            if (d && d.length >= 7) months.add(d.replace(/-/g, '/').substring(0, 7));
        });
        return Array.from(months).sort().reverse();
    },

    getOverview: async (targetMonth: string): Promise<{ records: ReconciliationRecord[], recurringStats: any, densaiPending: DensaiReservation[] }> => {
      const db = getDb();
      const targetMonthPrefix = targetMonth.replace('-', '/').substring(0, 7);

      // 全 collection を並列取得（逐次 await を Promise.all 化することで体感速度を改善）
      const [
        billingSnap,
        depositsSnap,
        matchesSnap,
        autoRulesSnap,
        clientsSnap,
        recurringSnap,
        densaiSnap,
      ] = await Promise.all([
        getDocs(collection(db, COLS.BILLING)),
        getDocs(collection(db, COLS.DEPOSITS)),
        getDocs(collection(db, COLS.MATCHES)),
        getDocs(collection(db, COLS.RULES_AUTO)),
        getDocs(collection(db, COLS.CLIENTS)),
        getDocs(collection(db, COLS.RULES_RECURRING)),
        getDocs(query(collection(db, COLS.DENSAI_RESERVATIONS), where('status', '==', 'pending'))),
      ]);

      const allBilling: BillingRecord[] = [];
      billingSnap.forEach(d => allBilling.push({ id: d.id, ...(d.data() as BillingRecord) }));

      const allDeposits: BankDeposit[] = [];
      depositsSnap.forEach(d => allDeposits.push({ id: d.id, ...(d.data() as BankDeposit) }));

      const allMatches: Match[] = [];
      matchesSnap.forEach(d => allMatches.push({ id: d.id, ...(d.data() as Match) }));

      const autoRules = autoRulesSnap.docs.map(d => d.data() as AutoOtherRule);

      const clientsMap = new Map();
      clientsSnap.forEach(d => {
          const client = d.data() as Client;
          clientsMap.set(client.client_code, client);
      });

      const recurringRulesList: RecurringPayment[] = [];
      recurringSnap.forEach(d => {
          const data = d.data() as RecurringPayment;
          if (!data.is_alias_only) {
              recurringRulesList.push(data);
          }
      });

      const densaiPending = densaiSnap.docs.map(d => ({id: d.id, ...(d.data() as DensaiReservation) } as DensaiReservation));

      const records: ReconciliationRecord[] = [];
      const matchedBillingIds = new Set();
      const matchedDepositIds = new Set();
      // 一括入金（allocated_deposit_amount 付き）は deposit_id でまとめて後処理
      const multiBillingBuffer = new Map<string, any[]>();

      allMatches.forEach(m => {
          if (m.match_type === MatchStatus.UNMATCHED) {
              // UNMATCHEDマッチはスキップ（表示ロジックは未照合入金の forEach で処理）
              return;
          }

          // 一括入金照合（allocated_deposit_amount 付き）はバッファに積んで後でまとめる
          if ((m as any).allocated_deposit_amount !== undefined) {
              if (m.billing_id) matchedBillingIds.add(m.billing_id);
              if (m.deposit_id) matchedDepositIds.add(m.deposit_id);
              const arr = multiBillingBuffer.get(m.deposit_id) || [];
              arr.push(m);
              multiBillingBuffer.set(m.deposit_id, arr);
              return;
          }

          const billing = m.billing_id ? allBilling.find(b => b.id === m.billing_id) : undefined;
          const deposit = allDeposits.find(d => d.id === m.deposit_id);

          if (billing || deposit) {
              if (billing) matchedBillingIds.add(billing.id);
              if (deposit) matchedDepositIds.add(deposit.id);

              const bMonth = billing?.import_month?.replace(/-/g, '/').substring(0, 7);
              
              let isRelevant = false;
              if (billing) {
                  isRelevant = (bMonth === targetMonthPrefix);
              } else if (deposit) {
                  // 入金のみのマッチ: 翌月払い前提で常に「入金月の前月」画面に表示
                  // 未照合入金と同じロジック（請求データの有無に依存しない）
                  const depDateStr = deposit.deposit_date?.replace(/-/g, '/');
                  const depMonth = depDateStr?.substring(0, 7);
                  if (depMonth) {
                      const [dy, dm] = depMonth.split('/').map(Number);
                      const prevDate = new Date(dy, dm - 2, 1);
                      const prevMonthStr = `${prevDate.getFullYear()}/${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
                      isRelevant = (prevMonthStr === targetMonthPrefix);
                  }
              }

              if (isRelevant) {
                  let resolvedClientName = '不明';
                  const client = clientsMap.get(m.client_code || billing?.client_code || '');
                  
                  if (client) {
                      resolvedClientName = client.client_name;
                  } else if (m.client_name) {
                      resolvedClientName = m.client_name;
                  } else if (billing?.client_name) {
                      resolvedClientName = billing.client_name;
                  } else if (m.match_type === MatchStatus.OTHER && deposit) {
                      const rule = autoRules.find(r => 
                          r.remitter_name === deposit.remitter_name || 
                          normalizeForComparison(r.remitter_name) === normalizeForComparison(deposit.remitter_name)
                      );
                      if (rule && rule.client_name) {
                          resolvedClientName = rule.client_name;
                      }
                  }

                  const bAmt = billing?.billing_balance || 0;
                  const dAmt = (m as any).allocated_deposit_amount ?? deposit?.amount ?? 0;

                  const isMonthly = recurringRulesList.some(r => {
                      const ruleAmt = r.expected_amount || 0;
                      const targetAmt = deposit ? deposit.amount : bAmt;
                      const isAmountMatched = ruleAmt === 0 || Math.abs(targetAmt - ruleAmt) <= 2000;
                      if (!isAmountMatched) return false;
                      if (r.client_code && r.client_code === (m.client_code || billing?.client_code)) return true;
                      if (deposit) {
                          const normRule = normalizeForComparison(r.remitter_name);
                          const normDeposit = normalizeForComparison(deposit.remitter_name);
                          // 銀行側が付加コードを追加する場合（入金名がルール名を含む）、
                          // または銀行側が末尾を切り捨てる場合（ルール名が入金名で始まる）に対応
                          if (normRule && (normRule === normDeposit || normDeposit.includes(normRule) || (normDeposit.length >= 4 && normRule.startsWith(normDeposit)))) return true;
                      }
                      return false;
                  });

                  // 同名得意先の候補コードを抽出
                  let candidateCodes: string[] | undefined;
                  if (m.notes && m.notes.includes('同名得意先が複数あり')) {
                      const codeMatch = m.notes.match(/得意先CD:\s*([^）]+)/);
                      if (codeMatch) {
                          candidateCodes = codeMatch[1].split(',').map(c => c.trim()).filter(c => c);
                      }
                  }
                  // 複数請求フラグ
                  const isMultiBillingClient = !!(m as any).multi_billing_client || (m.candidate_codes && m.notes && m.notes.includes('複数の請求あり'));

                  records.push({
                      id: m.id,
                      type: 'matched',
                      client_code: m.client_code || billing?.client_code || '',
                      client_name: resolvedClientName,
                      remitter_name: deposit?.remitter_name || '',
                      billing_month: billing?.import_month || '-',
                      billing_amount: bAmt,
                      deposit_amount: dAmt,
                      difference: bAmt - dAmt,
                      deposit_date: deposit?.deposit_date || '',
                      status: m.match_type,
                      notes: m.notes || '',
                      is_union: client?.is_union || false,
                      is_other: deposit?.is_other || false,
                      is_monthly: isMonthly && m.match_type !== MatchStatus.OTHER,
                      is_densai: deposit?.is_densai || false,
                      manually_approved: m.manually_approved,
                      billing_id: m.billing_id,
                      deposit_id: m.deposit_id,
                      candidate_codes: candidateCodes || (m as any).candidate_codes,
                      multi_billing_client: isMultiBillingClient || undefined,
                  });
              }
          }
      });

      // 一括入金グループを1行に統合して records に追加
      multiBillingBuffer.forEach((matches, depositId) => {
          const deposit = allDeposits.find(d => d.id === depositId);
          if (!deposit) return;

          // いずれかの請求が targetMonth に属するか確認
          const anyRelevant = matches.some(m => {
              const b = allBilling.find(b => b.id === m.billing_id);
              return b?.import_month?.replace(/-/g, '/').substring(0, 7) === targetMonthPrefix;
          });
          if (!anyRelevant) return;

          const billingCount = matches.length;
          const billingAmounts = matches.map((m: any) => {
              const b = allBilling.find(b => b.id === m.billing_id);
              return b?.billing_balance || 0;
          });
          const totalBillingAmt = billingAmounts.reduce((sum: number, a: number) => sum + a, 0);
          const actualDepositAmt = deposit.amount;
          const difference = totalBillingAmt - actualDepositAmt;

          const firstMatch = matches[0];
          const firstBilling = allBilling.find(b => b.id === firstMatch.billing_id);
          const clientCode = firstMatch.client_code || firstBilling?.client_code || '';
          const client = clientsMap.get(clientCode);
          let clientName = '不明';
          if (client) {
              clientName = client.client_name;
          } else if (firstMatch.client_name) {
              clientName = firstMatch.client_name;
          } else if (firstBilling?.client_name) {
              clientName = firstBilling.client_name;
          }

          const diffAbs = Math.abs(difference);
          const matchType = diffAbs === 0 ? MatchStatus.EXACT : diffAbs <= 1000 ? MatchStatus.WITH_FEE : MatchStatus.NEEDS_REVIEW;

          records.push({
              id: firstMatch.id,
              type: 'matched',
              client_code: clientCode,
              client_name: clientName,
              remitter_name: deposit.remitter_name,
              billing_month: firstBilling?.import_month || '-',
              billing_amount: totalBillingAmt,
              deposit_amount: actualDepositAmt,
              difference,
              deposit_date: deposit.deposit_date,
              status: matchType,
              notes: `手動照合（一括入金 ${billingCount}件）`,
              is_union: client?.is_union || false,
              is_other: deposit.is_other || false,
              is_monthly: false,
              is_densai: deposit.is_densai || false,
              manually_approved: true,
              billing_id: firstBilling?.id,
              deposit_id: depositId,
              billing_count: billingCount,
              billing_amounts: billingAmounts
          });
      });

      allBilling.forEach(b => {
          if (!matchedBillingIds.has(b.id)) {
              const bMonth = b.import_month?.replace(/-/g, '/').substring(0, 7);
              if (bMonth && bMonth === targetMonthPrefix) {
                  const client = clientsMap.get(b.client_code);
                  const isMonthly = recurringRulesList.some(r => r.client_code === b.client_code && (r.expected_amount === 0 || Math.abs(b.billing_balance - r.expected_amount) <= 2000));

                  records.push({
                      id: b.id,
                      type: 'billing',
                      client_code: b.client_code,
                      client_name: client?.client_name || b.client_name,
                      remitter_name: '-',
                      billing_month: b.import_month,
                      billing_amount: b.billing_balance,
                      deposit_amount: 0,
                      difference: b.billing_balance,
                      deposit_date: '-',
                      status: MatchStatus.UNMATCHED,
                      notes: b.notes || '',
                      is_union: client?.is_union || false,
                      is_other: false,
                      is_monthly: isMonthly,
                      is_densai: false,
                      manually_approved: false,
                      billing_id: b.id,
                      moved_from_month: b.moved_from_month
                  });
              }
          }
      });

      allDeposits.forEach(d => {
          if (!matchedDepositIds.has(d.id)) {
              // 未照合入金の表示月を一意に決定
              let isDepositRelevant = false;
              if (d.display_month) {
                  // 翌月移動済み：display_month が一致する月にのみ表示
                  isDepositRelevant = (d.display_month === targetMonthPrefix);
              } else if (d.deposit_date) {
                  // 翌月払い前提：未照合入金は常に「入金月の前月」画面に表示
                  // （請求データの有無に依存しない = 請求・入金の投入順序に左右されない）
                  const depDateStr = d.deposit_date.replace(/-/g, '/');
                  const depMonth = depDateStr.substring(0, 7);
                  if (depMonth) {
                      const [dy, dm] = depMonth.split('/').map(Number);
                      const prevDate = new Date(dy, dm - 2, 1);
                      const prevMonthStr = `${prevDate.getFullYear()}/${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
                      isDepositRelevant = (prevMonthStr === targetMonthPrefix);
                  }
              }
              if (isDepositRelevant) {
                  const client = d.client_code ? clientsMap.get(d.client_code) : null;
                  const isMonthly = recurringRulesList.some(r => {
                      const ruleAmt = r.expected_amount || 0;
                      const isAmountMatched = ruleAmt === 0 || Math.abs(d.amount - ruleAmt) <= 2000;
                      if (!isAmountMatched) return false;
                      if (d.client_code && r.client_code === d.client_code) return true;
                      const normRule = normalizeForComparison(r.remitter_name);
                      const normDeposit = normalizeForComparison(d.remitter_name);
                      // 銀行側が付加コードを追加する場合（入金名がルール名を含む）、
                      // または銀行側が末尾を切り捨てる場合（ルール名が入金名で始まる）に対応
                      return normRule ? (normRule === normDeposit || normDeposit.includes(normRule) || (normDeposit.length >= 4 && normRule.startsWith(normDeposit))) : false;
                  });

                  records.push({
                      id: d.id,
                      type: 'deposit',
                      client_code: d.client_code || '',
                      client_name: client?.client_name || '-',
                      remitter_name: d.remitter_name,
                      billing_month: '-',
                      billing_amount: 0,
                      deposit_amount: d.amount,
                      difference: -d.amount,
                      deposit_date: d.deposit_date,
                      status: MatchStatus.UNMATCHED,
                      notes: d.notes || '',
                      is_union: client?.is_union || false,
                      is_other: d.is_other || false,
                      is_monthly: isMonthly && !d.is_other,
                      is_densai: d.is_densai || false,
                      manually_approved: false,
                      deposit_id: d.id,
                      display_month: d.display_month
                  });
              }
          }
      });

      recurringRulesList.sort((a, b) => {
          if (a.client_code && !b.client_code) return -1;
          if (!a.client_code && b.client_code) return 1;
          return 0;
      });

      console.log('--- Recurring Stats Calculation Start ---');
      const usedRecordIds = new Set<string>();
      const paidDetails: string[] = [];
      const unpaidDetails: string[] = [];
      
      recurringRulesList.forEach((rule) => {
          const normRuleRemitter = normalizeForComparison(rule.remitter_name);
          if (!rule.client_code && (!normRuleRemitter || normRuleRemitter.length < 2)) {
              unpaidDetails.push(rule.remitter_name || '名称未設定');
              return;
          }
          
          const matchedRecord = records.find((r) => {
              if (usedRecordIds.has(r.id)) return false;
              if (r.status !== MatchStatus.EXACT && r.status !== MatchStatus.WITH_FEE) return false;

              const ruleAmt = rule.expected_amount || 0;
              const recordAmt = r.deposit_amount || r.billing_amount || 0;
              if (ruleAmt > 0 && Math.abs(recordAmt - ruleAmt) > 2000) {
                  return false;
              }
              
              if (rule.client_code && r.client_code && 
                  rule.client_code !== '未設定' && r.client_code !== '未設定' &&
                  r.client_code === rule.client_code) {
                  return true;
              }
              
              if (normRuleRemitter && r.remitter_name) {
                  const normRecordRemitter = normalizeForComparison(r.remitter_name);
                  if (normRecordRemitter === normRuleRemitter) return true;
                  if (normRuleRemitter.length >= 4 && normRecordRemitter.startsWith(normRuleRemitter)) return true;
                  if (normRecordRemitter.length >= 4 && normRuleRemitter.startsWith(normRecordRemitter)) return true;
              }
              
              if (rule.client_code) {
                  const normRecordClientName = normalizeForComparison(r.client_name || '');
                  const normRuleClientName = normalizeForComparison(rule.client_name || '');
                  if (normRuleClientName && normRecordClientName && normRecordClientName.includes(normRuleClientName)) return true;
              } else {
                  if (r.type === 'billing') {
                      const normRecordClientName = normalizeForComparison(r.client_name || '');
                      const normRuleClientName = normalizeForComparison(rule.client_name || '');
                      if (normRuleClientName && normRecordClientName && normRecordClientName === normRuleClientName) return true;
                  }
              }
              return false;
          });

          const name = rule.client_name || rule.remitter_name || '名称不明';
          if (matchedRecord) {
              usedRecordIds.add(matchedRecord.id);
              if (!paidDetails.includes(name)) paidDetails.push(name);
          } else {
              if (!unpaidDetails.includes(name)) unpaidDetails.push(name);
          }
      });
      console.log('--- Recurring Stats Calculation End ---');
      
      const paidRecurring = paidDetails.length;

      // 同名・別コードの後処理：全レコードを client_name でグループ化し、
      // 複数の client_code がある場合は全行に candidate_codes を補完
      const nameToCodesMap = new Map<string, Set<string>>();
      records.forEach(r => {
          if (!r.client_name || !r.client_code) return;
          const name = r.client_name.trim();
          if (!nameToCodesMap.has(name)) nameToCodesMap.set(name, new Set());
          nameToCodesMap.get(name)!.add(r.client_code);
      });
      records.forEach(r => {
          if (!r.client_name) return;
          const codes = nameToCodesMap.get(r.client_name.trim());
          if (codes && codes.size > 1 && !r.candidate_codes) {
              r.candidate_codes = Array.from(codes);
          }
      });

      return {
          records,
          recurringStats: {
              total: paidRecurring + unpaidDetails.length,
              paid: paidRecurring,
              unpaid: unpaidDetails.length,
              paidDetails,
              unpaidDetails
          },
          densaiPending: densaiPending
      };
    },

    /**
     * 紐付け解除履歴から該当する組み合わせを削除する
     *
     * 用途：手動照合で「学習する」にチェックを入れて保存したとき、
     * 該当の (得意先CD × 振込人名) ペアの履歴を削除し、翌月以降の自動照合で
     * 学習機能が機能するようにする。
     *
     * Firestore の deleteDoc は対象が存在しなくてもエラーを投げないため、
     * ここで catch される例外は権限エラーやネットワーク断などの実害ケース。
     * 呼び出し側で適切にハンドリングできるよう、エラーは握りつぶさず上位に再 throw する。
     */
    removeUnmatchHistory: async (clientCode: string, remitterName: string): Promise<void> => {
        if (!clientCode || !remitterName) return;
        const db = getDb();
        const bKey = getBlockingKey('billing', { client_code: clientCode });
        const dKey = getBlockingKey('deposit', { remitter_name: remitterName });
        if (!bKey || !dKey) return;
        const rawId = `${bKey}__${dKey}`;
        const id = rawId.replace(/\//g, '_');
        await deleteDoc(doc(db, COLS.UNMATCHED_HISTORY, id));
        log('Unmatch history removed for:', { clientCode, remitterName });
    },

    registerUnmatchHistory: async (record: ReconciliationRecord): Promise<void> => {
        const db = getDb();
        log('Attempting to register unmatch history for:', record);

        // 新フォーマット（v5.5.49〜）：得意先CD × 振込人名 のみで永続的にブロック
        // ガード条件もそれに合わせ、client_code と remitter_name の存在のみ確認する
        const bKey = record.client_code ? getBlockingKey('billing', { client_code: record.client_code }) : '';
        const dKey = record.remitter_name ? getBlockingKey('deposit', { remitter_name: record.remitter_name }) : '';

        if (bKey && dKey) {
            log('Generated Keys for Unmatch:', { bKey, dKey });
            const rawId = `${bKey}__${dKey}`;
            const id = rawId.replace(/\//g, '_');

            await setDoc(doc(db, COLS.UNMATCHED_HISTORY, id), {
                bKey,
                dKey,
                created_at: new Date().toISOString(),
                original_client_name: record.client_name,
                original_remitter_name: record.remitter_name,
            });
            log('Unmatch history saved successfully.');
        } else {
            console.warn('Skipped unmatch history registration. Missing client_code or remitter_name.', { bKey, dKey, record });
        }
    },


    runAutoMatch: async (): Promise<number> => {
        const db = getDb();
        // 全 collection を並列取得（逐次 await を Promise.all 化することで体感速度を改善）
        const [
            billingSnap,
            depositsSnap,
            matchesSnap,
            autoRulesSnap,
            recurringSnap,
            clientsSnap,
            paymentTermsSnap,
            unmatchedHistorySnap,
        ] = await Promise.all([
            getDocs(collection(db, COLS.BILLING)),
            getDocs(collection(db, COLS.DEPOSITS)),
            getDocs(collection(db, COLS.MATCHES)),
            getDocs(collection(db, COLS.RULES_AUTO)),
            getDocs(collection(db, COLS.RULES_RECURRING)),
            getDocs(collection(db, COLS.CLIENTS)),
            getDocs(collection(db, COLS.UNION_PAYMENT_TERMS)),
            // 紐付け解除履歴：再発防止のため、過去に解除された組み合わせを再生成しない
            getDocs(collection(db, COLS.UNMATCHED_HISTORY)),
        ]);
        const blockedCombinations: string[] = unmatchedHistorySnap.docs
            .map(d => {
                const data = d.data();
                if (!data.bKey || !data.dKey) return null;
                return `${data.bKey}__${data.dKey}`;
            })
            .filter((v): v is string => v !== null);

        const clientsMap = new Map<string, { kana: string, abbreviation: string, name: string, importedNames: string[] }>();
        clientsSnap.forEach(d => {
            const data = d.data() as Client;
            // imported_nameはカンマ・読点・スペース区切りのキーワードリスト
            const importedNames = (data.imported_name || '')
                .split(/[,、\s　]+/)
                .map((k: string) => k.trim())
                .filter((k: string) => k.length >= 2);
            clientsMap.set(data.client_code, {
                kana: data.client_kana || '',
                abbreviation: data.client_abbreviation || '',
                name: data.client_name || '',
                importedNames
            });
        });

        const billing = billingSnap.docs.map(d => {
            const data = d.data() as BillingRecord;
            const clientInfo = clientsMap.get(data.client_code);
            const record = {
                id: d.id,
                ...data,
                client_kana: data.client_kana || clientInfo?.kana || '',
                client_abbreviation: clientInfo?.abbreviation || '',
                // マスタ得意先名で補完（billing_recordsにclient_nameがない場合に備えて）
                client_name: (data as any).client_name || clientInfo?.name || '',
                // 照合名キーワードをworkerに渡す（自動照合での名称推論に使用）
                imported_names: clientInfo?.importedNames || []
            } as any;
            record._blockingKey = getBlockingKey('billing', record);
            return record;
        });

        const deposits = depositsSnap.docs.map(d => {
            const data = d.data() as BankDeposit;
            const record = { id: d.id, ...data } as BankDeposit;
            (record as any)._blockingKey = getBlockingKey('deposit', record);
            return record;
        });

        const matches = matchesSnap.docs.map(d => ({ id: d.id, ...(d.data() as Match) }));
        const autoRules = autoRulesSnap.docs.map(d => d.data());
        const recurringRules = recurringSnap.docs.map(d => d.data());

        // 支払サイクルマップ（client_code → payment_cycle）を生成
        const paymentCycleMap: { [clientCode: string]: number } = {};
        paymentTermsSnap.forEach(d => {
            const data = d.data();
            if (data.client_code && data.payment_cycle) {
                paymentCycleMap[data.client_code] = data.payment_cycle;
            }
        });

        const result = await runAutoMatchingWorker(billing, deposits, matches, autoRules, recurringRules, paymentCycleMap, blockedCombinations);
        
        if (result.matches.length > 0) {
            let batch = writeBatch(db);
            let count = 0;
            for (const m of result.matches) {
                const id = generateUuid();
                batch.set(doc(db, COLS.MATCHES, id), sanitize({ ...m, id, created_at: new Date().toISOString() }));
                count++;
                if (count % BATCH_LIMIT === 0) {
                    await batch.commit();
                    batch = writeBatch(db);
                }
            }
            if (count % BATCH_LIMIT !== 0) await batch.commit();
        }
        return result.count;
    },

    updateSourceRecord: async (record: ReconciliationRecord): Promise<void> => {
        const db = getDb();
        if (record.type === 'billing') {
            await updateDoc(doc(db, COLS.BILLING, record.id), {
                billing_balance: record.billing_amount,
                ...(record.client_code ? { client_code: record.client_code } : {}),
                ...(record.client_name ? { client_name: record.client_name } : {}),
                notes: record.notes ?? ''
            });
        } else if (record.type === 'deposit') {
            const updateData: any = { amount: record.deposit_amount, notes: record.notes ?? '' };
            if (record.remitter_name) updateData.remitter_name = record.remitter_name;
            if (record.client_code) updateData.client_code = record.client_code;
            await updateDoc(doc(db, COLS.DEPOSITS, record.id), updateData);
        }
    },

    createMatch: async (data: any): Promise<void> => {
        const db = getDb();
        const id = generateUuid();
        await setDoc(doc(db, COLS.MATCHES, id), sanitize({ ...data, id, created_at: new Date().toISOString() }));
    },

    deleteMatch: async (matchId: string): Promise<void> => {
        const db = getDb();
        await deleteDoc(doc(db, COLS.MATCHES, matchId));
    },

    /**
     * 紐付けをやめる：matches 削除 ＋ 解除履歴登録（再発防止）
     *
     * - record.type === 'matched' のレコードのみ対象
     * - 一括入金（同じ deposit_id に複数 match）に対応：
     *   getOverview は一括入金グループを 1 行に統合して record.id に先頭 match のみを入れるため、
     *   ここで同じ deposit_id を持つ全 match を Firestore から取得し、それぞれを削除する。
     * - 各 match の (client_code, remitter_name) ペアを履歴登録して再発防止
     */
    unlinkMatch: async (record: ReconciliationRecord): Promise<void> => {
        if (record.type !== 'matched' || !record.id) {
            throw new Error('紐付けをやめる対象は matched レコードのみです');
        }
        const db = getDb();

        // 削除対象 matches を特定
        // 通常は record.id の 1 件だが、一括入金の場合は同じ deposit_id を持つ matches 全部が対象
        const targetMatches: { id: string; data: Match }[] = [];
        if (record.deposit_id) {
            const allMatchesSnap = await getDocs(collection(db, COLS.MATCHES));
            allMatchesSnap.docs.forEach(d => {
                const m = d.data() as Match;
                if (m.deposit_id === record.deposit_id) {
                    targetMatches.push({ id: d.id, data: m });
                }
            });
        }
        // フォールバック：deposit_id が無い、または上記で見つからなかった場合は record.id のみ
        if (targetMatches.length === 0) {
            const single = await getDoc(doc(db, COLS.MATCHES, record.id));
            if (single.exists()) {
                targetMatches.push({ id: single.id, data: single.data() as Match });
            }
        }

        // 履歴登録（remitter_name は record から取得・各 match の client_code を使う）
        for (const { data } of targetMatches) {
            if (data.client_code && record.remitter_name) {
                const dummyRecord: ReconciliationRecord = {
                    ...record,
                    client_code: data.client_code,
                    client_name: data.client_name || record.client_name,
                };
                await api.reconciliation.registerUnmatchHistory(dummyRecord);
            }
        }

        // matches 一括削除（writeBatch で原子性確保）
        let batch = writeBatch(db);
        let count = 0;
        for (const { id } of targetMatches) {
            batch.delete(doc(db, COLS.MATCHES, id));
            count++;
            if (count % BATCH_LIMIT === 0) {
                await batch.commit();
                batch = writeBatch(db);
            }
        }
        if (count % BATCH_LIMIT !== 0) await batch.commit();
    },

    getCandidateBilling: async (clientCodes: string[], targetMonth: string): Promise<BillingRecord[]> => {
        const db = getDb();
        const snap = await getDocs(collection(db, COLS.BILLING));
        const monthPrefix = targetMonth.replace(/-/g, '/').substring(0, 7);
        const results: BillingRecord[] = [];
        snap.forEach(d => {
            const b = { id: d.id, ...(d.data() as BillingRecord) };
            const bMonth = b.import_month?.replace(/-/g, '/').substring(0, 7);
            if (clientCodes.includes(b.client_code) && bMonth === monthPrefix) {
                results.push(b);
            }
        });
        return results;
    },

    getMatchesForClient: async (clientCode: string, targetMonth: string): Promise<{ matchId: string, billingId: string, depositId: string, billingAmount: number, depositAmount: number, clientName: string, notes: string }[]> => {
        const db = getDb();
        const monthPrefix = targetMonth.replace(/-/g, '/').substring(0, 7);
        const matchSnap = await getDocs(collection(db, COLS.MATCHES));
        const results: any[] = [];
        for (const d of matchSnap.docs) {
            const m = d.data();
            if (m.client_code !== clientCode) continue;
            if (m.match_type === MatchStatus.UNMATCHED) continue;
            // Check billing month
            if (m.billing_id) {
                const billingDoc = await getDoc(doc(db, COLS.BILLING, m.billing_id));
                if (billingDoc.exists()) {
                    const b = billingDoc.data();
                    const bMonth = (b.import_month || '').replace(/-/g, '/').substring(0, 7);
                    if (bMonth !== monthPrefix) continue;
                    let depositAmount = 0;
                    if (m.deposit_id) {
                        const depDoc = await getDoc(doc(db, COLS.DEPOSITS, m.deposit_id));
                        if (depDoc.exists()) depositAmount = Number(depDoc.data().amount || 0);
                    }
                    results.push({
                        matchId: d.id,
                        billingId: m.billing_id,
                        depositId: m.deposit_id || '',
                        billingAmount: Number(b.billing_balance || 0),
                        depositAmount,
                        clientName: b.client_name || m.client_name || '',
                        notes: m.notes || '',
                    });
                }
            }
        }
        return results;
    },

    switchMatchBilling: async (matchId: string, newBillingId: string, newClientCode: string): Promise<void> => {
        const db = getDb();
        await updateDoc(doc(db, COLS.MATCHES, matchId), {
            billing_id: newBillingId,
            client_code: newClientCode,
            match_type: MatchStatus.NEEDS_REVIEW,
            notes: '候補選択により紐付け変更',
            manually_approved: false
        });
    },

    getUnmatchedBillingsForManualMatch: async (targetMonth: string, includeBillingIds?: string[]): Promise<BillingRecord[]> => {
        const db = getDb();
        const [billingsSnap, matchesSnap] = await Promise.all([
            getDocs(collection(db, COLS.BILLING)),
            getDocs(collection(db, COLS.MATCHES))
        ]);

        // マッチ済み請求IDを収集（UNMATCHED扱いのマッチは除外）
        const matchedBillingIds = new Set<string>();
        matchesSnap.docs.forEach(d => {
            const m = d.data();
            if (m.match_type !== MatchStatus.UNMATCHED && m.billing_id) {
                matchedBillingIds.add(m.billing_id);
            }
        });

        // 対象月から最大3ヶ月前まで（全支払サイクルをカバー）
        const [y, mo] = targetMonth.replace(/-/g, '/').substring(0, 7).split('/').map(Number);
        const allowedMonths = new Set<string>();
        for (let i = 0; i <= 3; i++) {
            const d = new Date(y, mo - 1 - i, 1);
            allowedMonths.add(`${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}`);
        }

        const results: BillingRecord[] = [];
        billingsSnap.docs.forEach(d => {
            const b = { id: d.id, ...(d.data() as BillingRecord) };
            const bMonth = b.import_month?.replace(/-/g, '/').substring(0, 7);
            if (!bMonth || !allowedMonths.has(bMonth)) return;
            // includeBillingIds（現在選択中）でない場合、照合済みフラグを付与
            const isIncluded = includeBillingIds?.includes(b.id) ?? false;
            if (matchedBillingIds.has(b.id) && !isIncluded) {
                b.already_matched = true;
            }
            results.push(b);
        });

        return results.sort((a, b) => (a.client_name || '').localeCompare(b.client_name || '', 'ja'));
    },

    getExistingMatchBillingsForDeposit: async (depositId: string): Promise<string[]> => {
        const db = getDb();
        const snap = await getDocs(
            query(collection(db, COLS.MATCHES), where('deposit_id', '==', depositId))
        );
        return snap.docs
            .filter(d => (d.data() as Match).match_type !== MatchStatus.UNMATCHED)
            .map(d => (d.data() as Match).billing_id)
            .filter((id): id is string => !!id);
    },

    createManualMatches: async (params: {
        depositId: string;
        depositAmount: number;
        billings: { id: string; billing_balance: number; client_code: string; client_name: string }[];
    }): Promise<void> => {
        const db = getDb();
        const { depositId, depositAmount, billings } = params;
        const isMultiBilling = billings.length > 1;
        const selectedBillingIds = new Set(billings.map(b => b.id));

        // 全マッチを取得して、このdepositの既存マッチ＋選択した請求が別depositに紐づくマッチを削除
        const allMatchesSnap = await getDocs(collection(db, COLS.MATCHES));

        const batch = writeBatch(db);

        allMatchesSnap.docs.forEach(d => {
            const m = d.data() as Match;
            if (m.match_type === MatchStatus.UNMATCHED) return;
            // このdepositの既存マッチを削除
            if (m.deposit_id === depositId) {
                batch.delete(d.ref);
                return;
            }
            // 選択した請求が別depositに紐づいていた場合も解除
            if (m.billing_id && selectedBillingIds.has(m.billing_id)) {
                batch.delete(d.ref);
            }
        });

        // 請求ごとにマッチを作成
        for (const billing of billings) {
            const id = generateUuid();
            const diff = isMultiBilling ? 0 : Math.abs(billing.billing_balance - depositAmount);
            const matchType = diff === 0 ? MatchStatus.EXACT : diff <= 1000 ? MatchStatus.WITH_FEE : MatchStatus.MANUAL_MATCH;
            const matchData: any = {
                id,
                billing_id: billing.id,
                deposit_id: depositId,
                client_code: billing.client_code,
                client_name: billing.client_name,
                match_type: matchType,
                manually_approved: true,
                notes: isMultiBilling ? '手動照合（一括入金）' : '手動照合',
                created_at: new Date().toISOString()
            };
            // 一括入金の場合は各請求に対応する入金配分額を記録
            if (isMultiBilling) {
                matchData.allocated_deposit_amount = billing.billing_balance;
            }
            batch.set(doc(db, COLS.MATCHES, id), sanitize(matchData));
        }

        await batch.commit();
    },

    updateRecord: async (record: ReconciliationRecord): Promise<void> => {
        const db = getDb();
        if (record.type === 'matched') {
            const updateData: any = {
                match_type: record.status,
                notes: record.notes,
                manually_approved: record.manually_approved
            };
            await updateDoc(doc(db, COLS.MATCHES, record.id), updateData);
            // 金額変更がある場合、元の請求/入金レコードも更新
            if (record.billing_id && record.billing_amount !== undefined) {
                await updateDoc(doc(db, COLS.BILLING, record.billing_id), {
                    billing_balance: record.billing_amount
                });
            }
            if (record.deposit_id && record.deposit_amount !== undefined) {
                await updateDoc(doc(db, COLS.DEPOSITS, record.deposit_id), {
                    amount: record.deposit_amount
                });
            }
        }
    },

    deleteRecords: async (records: ReconciliationRecord[]): Promise<void> => {
        const db = getDb();
        let batch = writeBatch(db);
        let count = 0;
        for (const r of records) {
            if (r.type === 'matched') {
                batch.delete(doc(db, COLS.MATCHES, r.id));
                count++;
            }
            if (r.billing_id) {
                batch.delete(doc(db, COLS.BILLING, r.billing_id));
                count++;
            }
            if (r.deposit_id) {
                batch.delete(doc(db, COLS.DEPOSITS, r.deposit_id));
                count++;
            }
            if (count >= BATCH_LIMIT - 5) {
                await batch.commit();
                batch = writeBatch(db);
                count = 0;
            }
        }
        if (count > 0) await batch.commit();
    },

    importBankData: async (data: any[]): Promise<{ imported: number, skipped: number }> => {
        const db = getDb();
        const existingSnap = await getDocs(collection(db, COLS.DEPOSITS));
        const existingKeys = new Set();
        existingSnap.forEach(doc => {
            const d = doc.data() as BankDeposit;
            const key = `${(d.deposit_date || '').replace(/-/g, '/')}_${normalizeForComparison(d.remitter_name || '')}_${Number(d.amount)}`;
            existingKeys.add(key);
        });

        const toImport = data.filter(d => {
            const key = `${(d.deposit_date || '').replace(/-/g, '/')}_${normalizeForComparison(d.remitter_name || '')}_${Number(d.amount)}`;
            return !existingKeys.has(key);
        });

        const densaiQ = query(collection(db, COLS.DENSAI_RESERVATIONS), where('status', '==', 'pending'));
        const densaiSnap = await getDocs(densaiQ);
        const densaiReservations = densaiSnap.docs.map(doc => ({ id: doc.id, ...(doc.data() as DensaiReservation) } as DensaiReservation));

        const clientsSnap = await getDocs(collection(db, COLS.CLIENTS));
        const clientKanaMap = new Map<string, string>();
        clientsSnap.forEach(doc => {
            const client = doc.data() as Client;
            if (client.client_code && client.client_kana) {
                clientKanaMap.set(client.client_code, normalizeForComparison(client.client_kana));
            }
        });

        let batch = writeBatch(db);
        let count = 0;
        
        for (const d of toImport) {
            const id = generateUuid();
            let finalDeposit = { ...d, id, import_date: new Date().toISOString() };
            
            const matchedReservation = densaiReservations.find(res => {
                const dAmt = Number(d.amount);
                const rAmt = Number(res.amount);
                if (Math.abs(dAmt - rAmt) !== 0) return false;

                const dDate = new Date(d.deposit_date.replace(/-/g, '/'));
                const rDate = new Date(res.payment_date.replace(/-/g, '/'));
                if (isNaN(dDate.getTime()) || isNaN(rDate.getTime())) return false;
                
                const diffTime = Math.abs(dDate.getTime() - rDate.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
                if (diffDays > 7) return false; 

                const normDepositName = normalizeForComparison(d.remitter_name);
                const normResClientName = normalizeForComparison(res.client_name);
                const normResPdfName = normalizeForComparison(res.remitter_name_pdf);

                if (normDepositName.length >= 2) {
                    if (normResClientName.includes(normDepositName) || normDepositName.includes(normResClientName)) return true;
                    if (normResPdfName.includes(normDepositName) || normDepositName.includes(normResPdfName)) return true;

                    if (res.client_code) {
                        const masterKana = clientKanaMap.get(res.client_code);
                        if (masterKana) {
                            if (normDepositName.includes(masterKana) || masterKana.includes(normDepositName)) return true;
                        }
                    }
                }
                
                return false;
            });

            if (matchedReservation) {
                finalDeposit.client_code = matchedReservation.client_code;
                finalDeposit.is_densai = true;
                finalDeposit.memo = (finalDeposit.memo || '') + ` [でんさい予約消込：${matchedReservation.record_number}]`;

                batch.update(doc(db, COLS.DENSAI_RESERVATIONS, matchedReservation.id), {
                    status: 'completed',
                    linked_deposit_id: id
                });
                
                const idx = densaiReservations.indexOf(matchedReservation);
                if (idx > -1) densaiReservations.splice(idx, 1);
            }

            batch.set(doc(db, COLS.DEPOSITS, id), sanitize(finalDeposit));
            count++;
            if (count % BATCH_LIMIT === 0) {
                await batch.commit();
                batch = writeBatch(db);
            }
        }
        if (count % BATCH_LIMIT !== 0) await batch.commit();
        
        await api.reconciliation.runAutoMatch();

        return { imported: count, skipped: data.length - toImport.length };
    },

    carryOverBilling: async (recordId: string, currentMonthStr: string): Promise<void> => {
        const db = getDb();
        const [y, m] = currentMonthStr.replace('/', '-').split('-').map(Number);
        const date = new Date(y, m, 1);
        const nextYear = date.getFullYear();
        const nextMonth = date.getMonth() + 1;
        const nextMonthStr = `${nextYear}/${String(nextMonth).padStart(2, '0')}`;
        const fromMonth = currentMonthStr.replace('-', '/').substring(0, 7);

        await updateDoc(doc(db, COLS.BILLING, recordId), {
            import_month: nextMonthStr,
            moved_from_month: fromMonth,
            updated_at: new Date().toISOString()
        });
    },

    revertBilling: async (recordId: string, movedFromMonth: string): Promise<void> => {
        const db = getDb();
        await updateDoc(doc(db, COLS.BILLING, recordId), {
            import_month: movedFromMonth,
            moved_from_month: null,
            updated_at: new Date().toISOString()
        });
    },

    carryOverDeposit: async (depositId: string, nextMonthStr: string): Promise<void> => {
        const db = getDb();
        await updateDoc(doc(db, COLS.DEPOSITS, depositId), {
            display_month: nextMonthStr,
            updated_at: new Date().toISOString()
        });
    },

    revertDeposit: async (depositId: string): Promise<void> => {
        const db = getDb();
        await updateDoc(doc(db, COLS.DEPOSITS, depositId), {
            display_month: null,
            updated_at: new Date().toISOString()
        });
    },
  },

  union: {
    createBatch: async (unionType: 'satsuma' | 'minamisatsuma', yearMonth: string, records: any[]): Promise<void> => {
        const db = getDb();
        const batchId = generateUuid();
        
        const totalBilling = records.reduce((sum, r) => sum + (r.billing_amount || 0), 0);
        const totalDeposit = records.reduce((sum, r) => sum + (r.deposit_amount || 0), 0);

        const batch: UnionStatementBatch = {
            id: batchId,
            union_type: unionType,
            year_month: yearMonth,
            created_at: new Date().toISOString(),
            total_count: records.length,
            total_billing: totalBilling,
            total_deposit: totalDeposit,
            status: 'pending'
        };

        await setDoc(doc(db, COLS.UNION_BATCHES, batchId), sanitize(batch));

        let b = writeBatch(db);
        let count = 0;
        for (const [index, r] of records.entries()) {
            const recId = generateUuid();
            const record: UnionStatementRecord = {
                id: recId,
                batch_id: batchId,
                client_code: r.client_code,
                client_name: r.client_name,
                client_abbreviation: r.client_abbreviation,
                category: r.category,
                shipping: r.shipping,
                billing_amount: r.billing_amount,
                deposit_amount: r.deposit_amount,
                difference: r.difference,
                status: 'unmatched',
                notes: r.notes,
                sort_order: index
            };
            b.set(doc(db, COLS.UNION_RECORDS, recId), sanitize(record));
            count++;
            if (count % BATCH_LIMIT === 0) { await b.commit(); b = writeBatch(db); }
        }
        if (count % BATCH_LIMIT !== 0) await b.commit();
    },

    getBatches: async (): Promise<UnionStatementBatch[]> => {
        const db = getDb();
        const q = query(collection(db, COLS.UNION_BATCHES), orderBy('year_month', 'desc'));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...(d.data() as UnionStatementBatch) }));
    },

    updateBatch: async (id: string, data: Partial<UnionStatementBatch>): Promise<void> => {
        const db = getDb();
        await updateDoc(doc(db, COLS.UNION_BATCHES, id), data);
    },

    getPaginated: async (options: any): Promise<{ data: UnionStatementBatch[], lastVisible: any, firstVisible: any }> => {
        const db = getDb();
        const colRef = collection(db, COLS.UNION_BATCHES);
        const orderByField = options.orderByField || 'created_at';
        const direction = options.direction || 'desc';
        const limitCount = options.limitCount || 20;

        let q;
        if (options.startAfterDoc) {
             q = query(colRef, orderBy(orderByField, direction), startAfter(options.startAfterDoc), limit(limitCount));
        } else if (options.endBeforeDoc) {
             q = query(colRef, orderBy(orderByField, direction), endBefore(options.endBeforeDoc), limitToLast(limitCount)); 
        } else {
             q = query(colRef, orderBy(orderByField, direction), limit(limitCount));
        }

        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ id: d.id, ...(d.data() as UnionStatementBatch) }));
        
        return {
            data,
            lastVisible: snap.docs[snap.docs.length - 1],
            firstVisible: snap.docs[0]
        };
    },

    deleteBatches: async (ids: string[]): Promise<void> => {
        const db = getDb();
        for (const batchId of ids) {
             const q = query(collection(db, COLS.UNION_RECORDS), where('batch_id', '==', batchId));
             const snap = await getDocs(q);
             let b = writeBatch(db);
             let count = 0;
             for (const d of snap.docs) {
                 b.delete(doc(db, COLS.UNION_RECORDS, d.id));
                 count++;
                 if (count % BATCH_LIMIT === 0) { await b.commit(); b = writeBatch(db); }
             }
             if (count % BATCH_LIMIT !== 0) await b.commit();
             
             await deleteDoc(doc(db, COLS.UNION_BATCHES, batchId));
        }
    },

    getRecords: async (batchId: string): Promise<UnionStatementRecord[]> => {
        const db = getDb();
        const q = query(collection(db, COLS.UNION_RECORDS), where('batch_id', '==', batchId));
        const snap = await getDocs(q);
        const records = snap.docs.map(d => ({ ...(d.data() as UnionStatementRecord), id: d.id }));
        // 元PDFの並び順を再現する。sort_order未設定の旧レコードは末尾にまとめる。
        return records.sort((a, b) =>
            (a.sort_order ?? Number.MAX_SAFE_INTEGER) - (b.sort_order ?? Number.MAX_SAFE_INTEGER)
        );
    },

    deleteRecords: async (ids: string[]): Promise<void> => {
        const db = getDb();
        let b = writeBatch(db);
        let count = 0;
        for (const id of ids) {
            b.delete(doc(db, COLS.UNION_RECORDS, id));
            count++;
            if (count % BATCH_LIMIT === 0) { await b.commit(); b = writeBatch(db); }
        }
        if (count % BATCH_LIMIT !== 0) await b.commit();
    },

    saveRecord: async (record: UnionStatementRecord): Promise<void> => {
        const db = getDb();
        await setDoc(doc(db, COLS.UNION_RECORDS, record.id), sanitize(record));
    }
  },

  data: {
    initializeDemoData: async () => {},
    clearAll: async () => {
        const db = getDb();
        const targetCols = [COLS.BILLING, COLS.DEPOSITS, COLS.MATCHES, COLS.UNION_BATCHES, COLS.UNION_RECORDS, COLS.DENSAI_RESERVATIONS, COLS.UNMATCHED_HISTORY];
        for (const colName of targetCols) {
            const snap = await getDocs(collection(db, colName));
            let b = writeBatch(db);
            let count = 0;
            for (const d of snap.docs) {
                b.delete(doc(db, colName, d.id));
                count++;
                if (count % BATCH_LIMIT === 0) {
                    await b.commit();
                    b = writeBatch(db);
                }
            }
            if (count % BATCH_LIMIT !== 0) await b.commit();
        }
    },
    deleteDataByMonth: async (yearMonth: string, targets: string[]) => {
      console.log(`[deleteDataByMonth] Starting deletion for ${yearMonth}, targets:`, targets);
      const db = getDb();
      const ymSlash = yearMonth.replace('-', '/');
      const ymHyphen = yearMonth.replace('/', '-');

      // Firestore の "in" クエリは1回あたり最大30件まで
      const IN_CHUNK = 30;

      // 指定IDの配列に対するドキュメント削除（batch化）
      const deleteByIds = async (colName: string, ids: string[]) => {
          if (ids.length === 0) return;
          let b = writeBatch(db);
          let count = 0;
          for (const id of ids) {
              b.delete(doc(db, colName, id));
              count++;
              if (count % BATCH_LIMIT === 0) { await b.commit(); b = writeBatch(db); }
          }
          if (count % BATCH_LIMIT !== 0) await b.commit();
      };

      // 指定IDを参照する matches を削除
      // billing_id または deposit_id が指定IDに該当する matches が削除対象
      // パフォーマンス最適化：チャンク単位の matches クエリを並列実行（Promise.all）
      const deleteRelatedMatches = async (idField: 'billing_id' | 'deposit_id', ids: string[]) => {
          if (ids.length === 0) return;

          // matches を並列取得（順次実行よりも大幅に高速）
          const queryPromises: Promise<any>[] = [];
          for (let i = 0; i < ids.length; i += IN_CHUNK) {
              const chunk = ids.slice(i, i + IN_CHUNK);
              const q = query(collection(db, COLS.MATCHES), where(idField, 'in', chunk));
              queryPromises.push(getDocs(q));
          }
          const snapshots = await Promise.all(queryPromises);

          // 取得した matches をバッチ削除
          let b = writeBatch(db);
          let count = 0;
          for (const snap of snapshots) {
              for (const d of snap.docs) {
                  b.delete(doc(db, COLS.MATCHES, d.id));
                  count++;
                  if (count % BATCH_LIMIT === 0) { await b.commit(); b = writeBatch(db); }
              }
          }
          if (count % BATCH_LIMIT !== 0 && count > 0) await b.commit();
          if (count > 0) console.log(`[deleteDataByMonth] Related matches deleted (${idField}): ${count}件`);
      };

      // 既存の関連削除（union_batches → union_records）用ヘルパー
      const deleteQueryBatch = async (colName: string, field: string, operator: any, value: any, relatedCollection?: string, relatedField?: string) => {
          const q = query(collection(db, colName), where(field, operator, value));
          const snap = await getDocs(q);
          let b = writeBatch(db);
          let count = 0;
          for (const d of snap.docs) {
              if (relatedCollection && relatedField) {
                  const subQ = query(collection(db, relatedCollection), where(relatedField, '==', d.id));
                  const subSnap = await getDocs(subQ);
                  for (const subD of subSnap.docs) {
                      b.delete(doc(db, relatedCollection, subD.id));
                      count++;
                      if (count % BATCH_LIMIT === 0) { await b.commit(); b = writeBatch(db); }
                  }
              }
              b.delete(doc(db, colName, d.id));
              count++;
              if (count % BATCH_LIMIT === 0) { await b.commit(); b = writeBatch(db); }
          }
          if (count % BATCH_LIMIT !== 0) await b.commit();
      };

      // 請求データ削除（関連 matches も連動削除）
      if (targets.includes('billing')) {
          const billingsSnap = await getDocs(query(collection(db, COLS.BILLING), where('import_month', '==', ymSlash)));
          const billingIds = billingsSnap.docs.map(d => d.id);
          await deleteRelatedMatches('billing_id', billingIds);
          await deleteByIds(COLS.BILLING, billingIds);
      }

      // 入金データ削除（関連 matches も連動削除）
      if (targets.includes('bank')) {
          const start = ymSlash + '/01';
          const end = ymSlash + '/31';
          const depositsSnap = await getDocs(query(
              collection(db, COLS.DEPOSITS),
              where('deposit_date', '>=', start),
              where('deposit_date', '<=', end)
          ));
          const depositIds = depositsSnap.docs.map(d => d.id);
          await deleteRelatedMatches('deposit_id', depositIds);
          await deleteByIds(COLS.DEPOSITS, depositIds);
      }

      // でんさい予約削除（matches とは独立）
      if (targets.includes('densai')) {
          const start = `${ymSlash}/01`;
          const [y, m] = ymSlash.split('/').map(Number);
          const nextMonth = m === 12 ? 1 : m + 1;
          const nextYear = m === 12 ? y + 1 : y;
          const nextMonthStart = `${nextYear}/${String(nextMonth).padStart(2, '0')}/01`;

          const q = query(
              collection(db, COLS.DENSAI_RESERVATIONS),
              where('payment_date', '>=', start),
              where('payment_date', '<', nextMonthStart)
          );
          const snap = await getDocs(q);
          await deleteByIds(COLS.DENSAI_RESERVATIONS, snap.docs.map(d => d.id));
      }

      // 組合データ削除（union_batches → union_records をカスケード）
      if (targets.includes('union')) {
          await deleteQueryBatch(COLS.UNION_BATCHES, 'year_month', '==', ymHyphen, COLS.UNION_RECORDS, 'batch_id');
          await deleteQueryBatch(COLS.UNION_BATCHES, 'year_month', '==', ymSlash, COLS.UNION_RECORDS, 'batch_id');
      }
    }
  }
};
